import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

export default function NFCCards({ isAdmin }) {
  const [cards, setCards] = useState([]);
  const [cardholders, setCardholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ card_uid: '', cardholder_id: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [editCard, setEditCard] = useState(null);

  useEffect(() => {
    fetchCards();
    fetchCardholders();
  }, []);

  const fetchCards = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('restaurant_nfc_cards')?.select('*, restaurant_cardholders(full_name, category, building)')?.order('created_at', { ascending: false });
      if (!error) setCards(data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchCardholders = async () => {
    const { data } = await supabase?.from('restaurant_cardholders')?.select('id, full_name, category')?.eq('is_active', true)?.order('full_name');
    setCardholders(data || []);
  };

  const handleSave = async () => {
    if (!form?.card_uid?.trim()) return;
    setSaving(true);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const payload = {
        card_uid: form?.card_uid?.trim()?.toUpperCase(),
        cardholder_id: form?.cardholder_id || null,
        notes: form?.notes,
        assigned_by: user?.id,
        assigned_at: form?.cardholder_id ? new Date()?.toISOString() : null,
      };
      let error;
      if (editCard) {
        ({ error } = await supabase?.from('restaurant_nfc_cards')?.update(payload)?.eq('id', editCard?.id));
      } else {
        ({ error } = await supabase?.from('restaurant_nfc_cards')?.insert(payload));
      }
      if (!error) {
        setShowForm(false);
        setEditCard(null);
        setForm({ card_uid: '', cardholder_id: '', notes: '' });
        fetchCards();
      }
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleToggleActive = async (card) => {
    await supabase?.from('restaurant_nfc_cards')?.update({ is_active: !card?.is_active })?.eq('id', card?.id);
    fetchCards();
  };

  const openEdit = (card) => {
    setEditCard(card);
    setForm({ card_uid: card?.card_uid, cardholder_id: card?.cardholder_id || '', notes: card?.notes || '' });
    setShowForm(true);
  };

  const filtered = cards?.filter(c => {
    if (!search) return true;
    const q = search?.toLowerCase();
    return c?.card_uid?.toLowerCase()?.includes(q) || c?.restaurant_cardholders?.full_name?.toLowerCase()?.includes(q);
  });

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <input type="text" placeholder="Search cards or holders..." value={search} onChange={e => setSearch(e?.target?.value)}
            className="border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 w-56" />
          <span className="text-xs text-muted-foreground">{filtered?.length} card(s)</span>
        </div>
        {isAdmin && (
          <button onClick={() => { setShowForm(true); setEditCard(null); setForm({ card_uid: '', cardholder_id: '', notes: '' }); }}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-all">
            <Icon name="CreditCard" size={14} />Register NFC Card
          </button>
        )}
      </div>
      {showForm && isAdmin && (
        <div className="bg-card border border-border rounded-xl p-5 space-y-4">
          <h3 className="font-semibold text-foreground">{editCard ? 'Edit NFC Card' : 'Register New NFC Card'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Card UID *</label>
              <input type="text" placeholder="e.g. NFC-A1B2C3D4" value={form?.card_uid} onChange={e => setForm(p => ({ ...p, card_uid: e?.target?.value }))}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Assign to Cardholder</label>
              <select value={form?.cardholder_id} onChange={e => setForm(p => ({ ...p, cardholder_id: e?.target?.value }))}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30">
                <option value="">— Unassigned —</option>
                {cardholders?.map(c => <option key={c?.id} value={c?.id}>{c?.full_name} ({c?.category})</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Notes</label>
              <input type="text" placeholder="Optional notes" value={form?.notes} onChange={e => setForm(p => ({ ...p, notes: e?.target?.value }))}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleSave} disabled={saving || !form?.card_uid?.trim()}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50 flex items-center gap-2 hover:opacity-90 transition-all">
              {saving ? <Icon name="Loader2" size={13} className="animate-spin" /> : <Icon name="Save" size={13} />}
              {editCard ? 'Update' : 'Register'}
            </button>
            <button onClick={() => { setShowForm(false); setEditCard(null); }}
              className="px-4 py-2 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-muted transition-all">Cancel</button>
          </div>
        </div>
      )}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12"><Icon name="Loader2" size={22} className="animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Card UID</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Assigned To</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Category</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Assigned At</th>
                  {isAdmin && <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered?.map(card => (
                  <tr key={card?.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-foreground">{card?.card_uid}</td>
                    <td className="px-4 py-3 text-foreground">{card?.restaurant_cardholders?.full_name || <span className="text-muted-foreground italic">Unassigned</span>}</td>
                    <td className="px-4 py-3">
                      {card?.restaurant_cardholders?.category && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium capitalize">{card?.restaurant_cardholders?.category}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${card?.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        {card?.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {card?.assigned_at ? new Date(card.assigned_at)?.toLocaleDateString() : '—'}
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => openEdit(card)} className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
                            <Icon name="Pencil" size={13} />
                          </button>
                          <button onClick={() => handleToggleActive(card)} className={`p-1.5 rounded-lg hover:bg-muted transition-colors ${card?.is_active ? 'text-amber-500' : 'text-emerald-500'}`}>
                            <Icon name={card?.is_active ? 'PauseCircle' : 'PlayCircle'} size={13} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
                {filtered?.length === 0 && (
                  <tr><td colSpan={isAdmin ? 6 : 5} className="py-10 text-center text-sm text-muted-foreground">No NFC cards found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
